/* footer tagline scramble/decode reveal

   ONE rAF loop drives every character, writing the whole tagline once per frame
   into a single text node. Do not reintroduce per-character setInterval timers
   or per-character spans: the earlier version ran 21 uncoordinated timers, each
   its own DOM write and layout of the footer subtree, measured at 4x CPU as
   110-150ms frames with 5-11 scramble callbacks stacked in one frame.

   Custom element rather than an init() pass: the theme editor replaces this
   node wholesale on shopify:section:load, and disconnected/connectedCallback
   re-arm the observer instead of leaving a dead one. */

(function () {
  'use strict';

  var TICK_MS = 45;
  var STAGGER_MS = 35;
  var MIN_TICKS = 7;
  var MAX_TICKS = 11;
  var REST_MIN_MS = 2000;
  var REST_MAX_MS = 3000;
  var CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ!?*';
  var LETTER = /[a-zA-Z]/;
  var REDUCE_QUERY = '(prefers-reduced-motion: reduce)';
  // scramble text - loops on desktop only; every tick wakes Clarity, which phones and in-app browsers pay for
  var DESKTOP_QUERY = '(min-width: 1024px)';

  function randomGlyph() {
    return CHARSET.charAt(Math.floor(Math.random() * CHARSET.length));
  }

  function randomTicks() {
    return MIN_TICKS + Math.floor(Math.random() * (MAX_TICKS - MIN_TICKS + 1));
  }

  function randomRestMs() {
    return REST_MIN_MS + Math.random() * (REST_MAX_MS - REST_MIN_MS);
  }

  function listenQuery(query, handler, on) {
    if (!query) return;
    if (query.addEventListener) query[on ? 'addEventListener' : 'removeEventListener']('change', handler);
    else if (query.addListener) query[on ? 'addListener' : 'removeListener'](handler);
  }

  class YgScrambleText extends HTMLElement {
    connectedCallback() {
      this._frame = 0;
      this._active = false;
      this._played = false;
      this._intersecting = false;
      this._pageVisible = document.visibilityState !== 'hidden';

      var text = this.getAttribute('data-text') || '';
      if (!text) return;

      this._text = text;
      this._chars = text.split('');

      this._letters = [];
      for (var i = 0; i < this._chars.length; i++) {
        if (LETTER.test(this._chars[i])) {
          this._letters.push({ index: i, delay: this._letters.length * STAGGER_MS, ticks: 0, tick: -1, glyph: '' });
        }
      }

      var display = document.createElement('span');
      display.className = 'yg-scramble-text__display';
      display.setAttribute('aria-hidden', 'true');
      this._node = document.createTextNode(text);
      display.appendChild(this._node);

      var label = document.createElement('span');
      label.className = 'visually-hidden';
      label.textContent = text;

      this.appendChild(display);
      this.appendChild(label);

      /* Locked to the correct text's rendered width so glyph-width jitter reflows
         inside a fixed box instead of pushing on the Brand column. Locked lazily
         in _lockWidth() rather than here: two instances of this tagline exist
         (one per breakpoint) and whichever the current breakpoint hides is
         display:none at connect time, so measuring now reads 0 forever. */
      display.style.display = 'inline-block';
      this._display = display;
      this._widthLocked = false;

      this._motion = window.matchMedia ? window.matchMedia(REDUCE_QUERY) : null;
      this._motionAllowed = !(this._motion && this._motion.matches);

      this._desktop = window.matchMedia ? window.matchMedia(DESKTOP_QUERY) : null;
      this._loop = !!(this._desktop && this._desktop.matches);
      this._onModeChange = this._onModeChange.bind(this);
      listenQuery(this._desktop, this._onModeChange, true);

      this._onIntersect = this._onIntersect.bind(this);
      this._arm = this._arm.bind(this);
      this._tick = this._tick.bind(this);
      this._onVisibilityChange = this._onVisibilityChange.bind(this);
      this._onMotionChange = this._onMotionChange.bind(this);
      this._listen();
    }

    disconnectedCallback() {
      this._stop();
      this._release();
      listenQuery(this._desktop, this._onModeChange, false);
    }

    _listen() {
      if (this._listening) return;
      this._listening = true;
      window.addEventListener('resize', this._arm);
      document.addEventListener('visibilitychange', this._onVisibilityChange);
      listenQuery(this._motion, this._onMotionChange, true);
      this._arm();
    }

    _release() {
      if (this._observer) this._observer.disconnect();
      this._observer = null;
      if (!this._listening) return;
      this._listening = false;
      window.removeEventListener('resize', this._arm);
      document.removeEventListener('visibilitychange', this._onVisibilityChange);
      listenQuery(this._motion, this._onMotionChange, false);
    }

    _onModeChange() {
      var loop = !!this._desktop.matches;
      if (loop === this._loop) return;
      this._loop = loop;
      if (loop && this._played) {
        this._played = false;
        this._listen();
      }
    }

    // footer tagline - the fixed footer always intersects, so wait until the reveal has uncovered it
    _arm() {
      if (this._played || !this._letters.length) return;
      if (this._observer) this._observer.disconnect();
      this._intersecting = false;

      var spacer = this.closest('.yg-footer-section') && this.offsetParent
        && !document.documentElement.classList.contains('yg-footer-reveal-off')
        ? document.querySelector('.yg-footer-reveal')
        : null;

      if (spacer && spacer.offsetHeight) {
        var inset = Math.max(0, Math.round(window.innerHeight - this._display.getBoundingClientRect().top));
        this._observer = new IntersectionObserver(this._onIntersect, { rootMargin: '0px 0px -' + inset + 'px 0px' });
        this._observer.observe(spacer);
      } else {
        this._observer = new IntersectionObserver(this._onIntersect, { threshold: 1 });
        this._observer.observe(this);
      }
    }

    _onIntersect(entries) {
      for (var i = 0; i < entries.length; i++) {
        this._intersecting = entries[i].isIntersecting;
      }
      this._sync();
    }

    _lockWidth() {
      var width = this._display.getBoundingClientRect().width;
      if (width > 0) {
        this._display.style.width = width + 'px';
        this._widthLocked = true;
      }
    }

    _onVisibilityChange() {
      this._pageVisible = document.visibilityState !== 'hidden';
      this._sync();
    }

    _onMotionChange() {
      this._motionAllowed = !(this._motion && this._motion.matches);
      this._sync();
    }

    _sync() {
      if (this._played) return;
      if (this._intersecting && this._pageVisible && this._motionAllowed) this._start();
      else if (this._loop) this._stop();
    }

    _start() {
      if (this._active || this._played || !this._letters.length) return;
      this._active = true;
      if (!this._widthLocked) this._lockWidth();
      if (!this._loop) {
        this._played = true;
        this._release();
      }
      this._beginCycle(performance.now());
      this._frame = requestAnimationFrame(this._tick);
    }

    _finish() {
      this._stop();
      this._played = true;
      this._release();
    }

    _stop() {
      this._active = false;
      if (this._frame) {
        cancelAnimationFrame(this._frame);
        this._frame = 0;
      }
      this._write(this._text);
    }

    _beginCycle(now) {
      this._cycleStart = now;
      var scrambleEnd = 0;
      for (var i = 0; i < this._letters.length; i++) {
        var letter = this._letters[i];
        letter.ticks = randomTicks();
        letter.tick = -1;
        var end = letter.delay + MAX_TICKS * TICK_MS;
        if (end > scrambleEnd) scrambleEnd = end;
      }
      this._scrambleEnd = scrambleEnd;
      this._cycleEnd = scrambleEnd + (this._loop ? randomRestMs() : 0);
    }

    _tick(now) {
      if (!this._active) return;

      var elapsed = now - this._cycleStart;
      if (elapsed >= this._scrambleEnd && !this._loop) {
        this._finish();
        return;
      }
      if (elapsed >= this._cycleEnd) {
        this._beginCycle(now);
        elapsed = 0;
      }

      var out = null;
      for (var i = 0; i < this._letters.length; i++) {
        var letter = this._letters[i];
        var into = elapsed - letter.delay;
        var glyph;

        if (into < 0) {
          glyph = this._chars[letter.index];
          letter.tick = -1;
        } else {
          var tick = Math.floor(into / TICK_MS);
          if (tick >= letter.ticks) {
            glyph = this._chars[letter.index];
            letter.tick = -1;
          } else {
            if (tick !== letter.tick) {
              letter.tick = tick;
              letter.glyph = randomGlyph();
            }
            glyph = letter.glyph;
          }
        }

        if (glyph !== this._chars[letter.index]) {
          if (!out) out = this._chars.slice();
          out[letter.index] = glyph;
        }
      }

      this._write(out ? out.join('') : this._text);
      this._frame = requestAnimationFrame(this._tick);
    }

    _write(value) {
      if (this._node && this._written !== value) {
        this._written = value;
        this._node.nodeValue = value;
      }
    }
  }

  if (!customElements.get('yg-scramble-text')) {
    customElements.define('yg-scramble-text', YgScrambleText);
  }
})();
